import { useState, useEffect } from 'react'
import { apiClient, handleApiRequest } from '@/services/api/client'
import { extractErrorMessage } from '@/utils/errorHandler'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { Badge } from '@/components/common/Badge'
import { Loader2, Search, CheckCircle, AlertTriangle, UserPlus, Link } from 'lucide-react'

interface Teacher {
  id: string
  teacher_code: string | null
  email: string
  full_name: string
}

interface UserSuggestion {
  user_id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: string
  email_verified: boolean
  match_method: string
  confidence: number
}

interface SuggestionsResponse {
  teacher_id: string
  teacher_code: string | null
  teacher_email: string
  teacher_full_name: string
  suggestions: UserSuggestion[]
}

interface TeacherUserLinkDialogProps {
  isOpen: boolean
  onClose: () => void
  teacher: Teacher
  onSuccess: () => void
  showCreateUser?: boolean
}

export function TeacherUserLinkDialog({
  isOpen,
  onClose,
  teacher,
  onSuccess,
  showCreateUser = false,
}: TeacherUserLinkDialogProps) {
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([])
  const [loading, setLoading] = useState(false)
  const [linking, setLinking] = useState(false)
  const [creatingUser, setCreatingUser] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<UserSuggestion[]>([])
  const [searching, setSearching] = useState(false)
  const [notes, setNotes] = useState('')
  const [showCreateUserForm, setShowCreateUserForm] = useState(showCreateUser)
  const [newUserEmail, setNewUserEmail] = useState('')
  const [sendInvitation, setSendInvitation] = useState(true)

  useEffect(() => {
    if (isOpen) {
      fetchSuggestions()
    }
  }, [isOpen, teacher.id])

  const fetchSuggestions = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await handleApiRequest<SuggestionsResponse>(
        apiClient.get(`/api/v1/admin/teachers/${teacher.id}/user-suggestions`)
      )
      setSuggestions(res.suggestions)
      if (res.suggestions.length > 0 && res.suggestions[0]) {
        setSelectedUserId(res.suggestions[0].user_id)
      }
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Vorschläge konnten nicht geladen werden' }))
    } finally {
      setLoading(false)
    }
  }

  const handleSearchUsers = async () => {
    if (!searchQuery.trim()) return
    setSearching(true)
    setError(null)
    try {
      const res = await handleApiRequest<{ users: UserSuggestion[] }>(
        apiClient.get(`/api/v1/admin/users/search?search=${encodeURIComponent(searchQuery)}&limit=10`)
      )
      setSearchResults(res.users)
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Suche fehlgeschlagen' }))
    } finally {
      setSearching(false)
    }
  }

  const handleLink = async () => {
    if (!selectedUserId) return
    setLinking(true)
    setError(null)
    try {
      await handleApiRequest(
        apiClient.post(`/api/v1/admin/teachers/${teacher.id}/link-user`, {
          user_id: selectedUserId,
          force: false,
          notes: notes,
        })
      )
      setSuccess('Lehrer erfolgreich verknüpft')
      setTimeout(() => onSuccess(), 1500)
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Verknüpfung fehlgeschlagen' }))
    } finally {
      setLinking(false)
    }
  }

  const handleCreateUser = async () => {
    setCreatingUser(true)
    setError(null)
    try {
      await handleApiRequest(
        apiClient.post(`/api/v1/admin/teachers/${teacher.id}/create-user`, {
          email: newUserEmail || undefined,
          send_invitation: sendInvitation,
          role: 'teacher',
        })
      )
      setSuccess('Benutzer erstellt und verknüpft')
      setTimeout(() => onSuccess(), 1500)
    } catch (err) {
      setError(extractErrorMessage(err, { loadError: 'Benutzererstellung fehlgeschlagen' }))
    } finally {
      setCreatingUser(false)
    }
  }

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 95) return <Badge variant="success">{confidence}%</Badge>
    if (confidence >= 80) return <Badge variant="warning">{confidence}%</Badge>
    return <Badge variant="default">{confidence}%</Badge>
  }

  const getMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      email: 'E-Mail',
      custom_fields: 'Code in Profil',
      name_exact: 'Name (exakt)',
      name_fuzzy: 'Name (ähnlich)',
    }
    return labels[method] || method
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Lehrer verknüpfen: ${teacher.teacher_code || teacher.full_name}`} size="lg">
      <div className="space-y-6">
        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            {success}
          </div>
        )}

        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Lehrer-Informationen</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-gray-500">Code:</span> {teacher.teacher_code || '-'}</div>
            <div><span className="text-gray-500">Name:</span> {teacher.full_name}</div>
            <div className="col-span-2"><span className="text-gray-500">E-Mail:</span> {teacher.email}</div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {suggestions.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Vorschläge ({suggestions.length})
                </h3>
                <div className="space-y-2">
                  {suggestions.map((suggestion) => (
                    <div
                      key={suggestion.user_id}
                      onClick={() => setSelectedUserId(suggestion.user_id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedUserId === suggestion.user_id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">
                            {suggestion.first_name} {suggestion.last_name}
                          </div>
                          <div className="text-sm text-gray-500">{suggestion.email}</div>
                          <div className="text-xs text-gray-400 mt-1">
                            Rolle: {suggestion.role} |
                            Verifiziert: {suggestion.email_verified ? 'Ja' : 'Nein'}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getConfidenceBadge(suggestion.confidence)}
                          <Badge variant="outline">{getMethodLabel(suggestion.match_method)}</Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {suggestions.length === 0 && !showCreateUserForm && (
              <div className="text-center py-4 text-gray-500">
                Keine passenden Benutzer gefunden
              </div>
            )}

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Benutzer suchen</h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Name oder E-Mail eingeben..."
                  className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Button onClick={handleSearchUsers} disabled={searching}>
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              {searchResults.length > 0 && (
                <div className="mt-2 space-y-1">
                  {searchResults.map((user) => (
                    <div
                      key={user.user_id}
                      onClick={() => setSelectedUserId(user.user_id)}
                      className={`p-2 rounded border cursor-pointer ${
                        selectedUserId === user.user_id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-medium">{user.first_name} {user.last_name}</span>
                      <span className="text-gray-500 ml-2">{user.email}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Notizen</h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional: Bemerkungen zur Verknüpfung..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
              />
            </div>

            {!showCreateUserForm && (
              <div className="flex justify-between pt-4 border-t">
                <Button
                  onClick={() => setShowCreateUserForm(true)}
                  variant="outline"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Neuen Benutzer erstellen
                </Button>
                <div className="flex gap-2">
                  <Button onClick={onClose} variant="ghost">
                    Abbrechen
                  </Button>
                  <Button
                    onClick={handleLink}
                    disabled={!selectedUserId || linking}
                    variant="primary"
                  >
                    {linking ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Link className="w-4 h-4 mr-2" />
                    )}
                    Verknüpfen
                  </Button>
                </div>
              </div>
            )}

            {showCreateUserForm && (
              <div className="space-y-4 pt-4 border-t">
                <h3 className="text-sm font-medium text-gray-700">Neuen Benutzer erstellen</h3>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">
                    E-Mail-Adresse (optional, verwendet Lehrer-E-Mail wenn leer)
                  </label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder={teacher.email}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sendInvitation}
                    onChange={(e) => setSendInvitation(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <label className="text-sm text-gray-600">
                    Einladungs-E-Mail senden
                  </label>
                </div>
                <div className="flex justify-between">
                  <Button onClick={() => setShowCreateUserForm(false)} variant="ghost">
                    Zurück
                  </Button>
                  <Button
                    onClick={handleCreateUser}
                    disabled={creatingUser}
                    variant="primary"
                  >
                    {creatingUser ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <UserPlus className="w-4 h-4 mr-2" />
                    )}
                    Benutzer erstellen
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}